import { Injectable } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { FirebaseService } from './firebase.service';
import { AnalyticsEntry } from '../models/AnalyticsEntry';
import { FeedbackService } from './feedback.service';
import { StorageKey, StorageService } from './storage.service';
import { lastValueFrom } from 'rxjs';
import { AnalyticsData, AnalyticsDataCompleteMission, AnalyticsDataViewMission } from '../types/analytics';
import { equalTo, get, getDatabase, orderByChild, query, ref, update } from '@angular/fire/database';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  existingData: AnalyticsEntry[];

  constructor(
    private papa: Papa,
    private firebaseService: FirebaseService,
    private feedbackService: FeedbackService,
    private storageService: StorageService,
  ) {
    this.existingData = this.storageService.get(StorageKey.ANALYTICS_DATA) ?? [];

    this.existingData.push({
      startTime: new Date().toISOString(),
      lastActive: new Date().toISOString(),
      finishedMissions: 0,
    });

    this.saveAnalyticsData();
  }

  public updateLastActive() {
    this.existingData[this.existingData.length - 1].lastActive = new Date().toISOString();
    this.saveAnalyticsData();
    const uuid = this.storageService.get(StorageKey.UUID);

    if (uuid) {
      return this.firebaseService.updateRealtimeDatabase('users/' + uuid, { lastActive: new Date().toISOString(), uid: uuid });
    }
  }

  public async missionFinished(missionId: number) {
    this.existingData[this.existingData.length - 1].finishedMissions += 1;
    this.saveAnalyticsData();

    const userId = this.storageService.get(StorageKey.UUID);

    const itemsRef = ref(getDatabase(), 'analytics');
    const lastEntry = query(itemsRef, orderByChild('userId'), equalTo(userId));

    const snapshot = await get(lastEntry);
    if (!snapshot.exists()) throw new Error('No analytics data found');

    const [viewMissionAnalyticsData] = (
      (Object.values(await snapshot.val()) as AnalyticsData[]).filter((e) => e.event === 'viewMission') as AnalyticsDataViewMission[]
    )
      .filter((e) => e.missionId === missionId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    await this.updateMissionViewDurationSeconds(
      viewMissionAnalyticsData.uid,
      (new Date().getTime() - new Date(viewMissionAnalyticsData.timestamp).getTime()) / 1000,
    );

    return await this.pushAnalyticsEvent<AnalyticsDataCompleteMission>({
      event: 'completeMission',
      userId: userId,
      missionId: missionId,
    });
  }

  downloadDataLocally() {
    const csv = this.papa.unparse(
      this.existingData.map((a) => ({
        startTime: a.startTime,
        lastActive: a.lastActive,
        finishedMissions: a.finishedMissions,
        uuid: this.storageService.get(StorageKey.UUID),
      })),
    );

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;

    link.download = 'data.csv';

    link.style.display = 'none';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  sendUserDataEmail() {
    const csv = this.papa.unparse(
      this.existingData.map((a) => ({
        startTime: a.startTime,
        lastActive: a.lastActive,
        finishedMissions: a.finishedMissions,
        uuid: this.storageService.get(StorageKey.UUID),
      })),
    );

    return lastValueFrom(this.feedbackService.sendFeedback('Analytics data verstuurd via Help menu', csv));
  }

  public async viewMission(missionId: number) {
    const userUid = this.storageService.get(StorageKey.UUID);
    const analyticsDataUid = crypto.randomUUID();

    await this.pushAnalyticsEvent<AnalyticsDataViewMission>({
      event: 'viewMission',
      missionId: missionId,
      userId: userUid,
      uid: analyticsDataUid,
    });

    return analyticsDataUid;
  }

  public async pushAnalyticsEvent<T extends AnalyticsData>(data: Omit<T, 'timestamp'>) {
    return await this.firebaseService.insertData('analytics', { timestamp: new Date().toISOString(), ...data });
  }

  public async updateMissionViewDurationSeconds(analyticsDataUid: string, viewDurationSeconds: number) {
    const itemsRef = ref(getDatabase(), 'analytics');
    const q = query(itemsRef, orderByChild('uid'), equalTo(analyticsDataUid));

    const snapshot = await get(q);
    if (!snapshot.exists()) throw new Error('No analytics data found');

    const data = await snapshot.val();
    const [key] = Object.keys(data);

    await update(ref(getDatabase(), 'analytics/' + key), { viewDurationSeconds });
  }

  private saveAnalyticsData() {
    return this.storageService.set(StorageKey.ANALYTICS_DATA, this.existingData);
  }
}
